/**
 * Module dependencies.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { Title } from 'app/components/atoms/title/title';
import { Spinner } from 'app/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'app/components/ui/tabs';
import { DataTable } from 'app/components/ui/data-table';
import { MultiSelectFilter } from 'app/components/ui/multi-select-filter';
import { Checkbox } from 'app/components/ui/checkbox';
import { useQueryState } from 'app/hooks/use-query-state';
import { AreaTrendChart, chartColors } from 'app/components/organisms/charts/charts';
import { useSetBreadcrumbTrail } from 'app/components/templates/dashboard/app-shell/breadcrumb-provider';
import {
  CgAssetDetail,
  useCryptoGestAsset,
  useCryptoGestAssetPriceHistory,
  useCryptoGestSettings,
  useSaveCryptoGestChartInterval
} from 'app/hooks/requests/crypto-gest/use-crypto-gest';
import { appRoutes } from 'app/routes';
import { ENTRY_TYPE_ORDER, amount, paddedDomain, pct, signClass } from '../format';
import { useDisplayMoney } from '../use-display-money';
import { StatCard } from '../stat-card';
import { AssetIcon } from '../asset-icon';
import { AssetWalletTransactions } from './crypto-gest-asset-transactions';
import { cn } from 'app/lib/utils';
import moment from 'moment';

/**
 * Price steps → Binance kline interval. The full series (back to the pair's
 * listing, capped) is fetched and shown in a scrollable chart so you can pan
 * back in time.
 */

const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/**
 * Candles shown per window. Panning moves back/forward one window at a time.
 */

const WINDOW_CANDLES = 120;

const PRICE_STEPS: Array<{ key: string; label: string; interval: string; ms: number; intraday: boolean }> = [
  { key: '15m', label: '15m', interval: '15m', ms: 15 * MIN, intraday: true },
  { key: '30m', label: '30m', interval: '30m', ms: 30 * MIN, intraday: true },
  { key: '1h', label: '1h', interval: '1h', ms: HOUR, intraday: true },
  { key: '4h', label: '4h', interval: '4h', ms: 4 * HOUR, intraday: true },
  { key: '12h', label: '12h', interval: '12h', ms: 12 * HOUR, intraday: true },
  { key: '1d', label: '24h', interval: '1d', ms: DAY, intraday: false },
  { key: '3d', label: '3d', interval: '3d', ms: 3 * DAY, intraday: false },
  { key: '1w', label: '1 sem', interval: '1w', ms: 7 * DAY, intraday: false },
  { key: '1M', label: '1 mês', interval: '1M', ms: 30 * DAY, intraday: false }
];

/**
 * A titled chart card.
 */

function ChartCard({
  title,
  action,
  children
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className={'min-w-0 rounded-xl border border-border bg-card p-4 shadow-sm'}>
      <div className={'mb-3 flex flex-wrap items-center justify-between gap-2'}>
        <div className={'text-sm font-semibold text-foreground'}>{title}</div>

        {action}
      </div>

      {children}
    </div>
  );
}

/**
 * Export `CryptoGestAssetDetail` component.
 */

export function CryptoGestAssetDetail(): JSX.Element {
  const [translate] = useTranslation();
  const { assetId } = useParams();
  const [searchParams] = useSearchParams();
  const { format: usd } = useDisplayMoney();
  const { data, isLoading } = useCryptoGestAsset(assetId);
  const { data: settings } = useCryptoGestSettings();
  const saveInterval = useSaveCryptoGestChartInterval();
  // Persisted per user; a local override applies immediately while it saves.
  const [stepOverride, setStepOverride] = useState<string | null>(null);
  const step = stepOverride ?? settings?.priceInterval ?? '1d';
  // How many windows back in time (0 = most recent).
  const [offset, setOffset] = useState(0);
  // Which entry types the table shows, persisted in the URL (?types=buy,sell).
  // Absent = all (nothing filtered); "-" = none selected; else a CSV of types.
  const [typesParam, setTypesParam] = useQueryState('types', '');
  // Whether pool-allocated ("hidden") transactions are shown — off by default,
  // persisted in the URL (?hidden=1) so the view can be linked/reloaded.
  const [hiddenParam, setHiddenParam] = useQueryState('hidden', '');
  const showHidden = hiddenParam === '1';

  const priceStep = PRICE_STEPS.find(item => item.key === step) ?? PRICE_STEPS[5];
  const windowMs = WINDOW_CANDLES * priceStep.ms;
  // The window's start, stable per (step, offset) — recomputing Date.now() each
  // render would churn the query key.
  const startTime = useMemo(() => Date.now() - (offset + 1) * windowMs, [offset, windowMs]);
  const { data: priceHistory } = useCryptoGestAssetPriceHistory(assetId, priceStep.interval, startTime, WINDOW_CANDLES);

  // Keep the wallet in the breadcrumb when the asset was opened from one
  // (?wallet=<id>), so we don't lose that navigation: "Wallets > <name> > BTC".
  // Otherwise fall back to the default section ("Overview") + the symbol.
  const originWalletId = searchParams.get('wallet');
  const originWallet = originWalletId ? data?.wallets.find(wallet => wallet.walletId === originWalletId) : undefined;

  useSetBreadcrumbTrail(
    !data
      ? null
      : originWallet
      ? [
          { label: translate('cryptoGest.menu.wallets'), pathname: appRoutes.dashboard.cryptoGest.wallets },
          {
            label: originWallet.name,
            pathname: `${appRoutes.dashboard.cryptoGest.wallets}/${originWallet.walletId}`
          },
          { label: data.symbol }
        ]
      : data.symbol
  );

  // Memoized (stable references) so Recharts doesn't restart its animation on
  // every re-render.
  const priceSeries = useMemo(
    () =>
      (priceHistory?.candles ?? []).map(point => ({
        date: moment(point.timestamp).format(priceStep.intraday ? 'DD MMM HH:mm' : 'DD MMM'),
        fullLabel: moment(point.timestamp).format(priceStep.intraday ? 'DD MMM YYYY, HH:mm' : 'DD MMM YYYY'),
        price: point.price
      })),
    [priceHistory, priceStep.intraday]
  );

  const priceDomain = useMemo(() => paddedDomain(priceSeries.map(point => point.price)), [priceSeries]);

  const selectStep = (key: string) => {
    setStepOverride(key);
    setOffset(0);
    saveInterval.mutate({ priceInterval: key });
  };

  const valueSeries = useMemo(
    () =>
      (data?.valueHistory ?? []).map(point => ({
        date: moment(point.timestamp).format('DD MMM'),
        fullLabel: moment(point.timestamp).format('DD MMM YYYY'),
        value: point.value
      })),
    [data]
  );

  const valueDomain = useMemo(() => paddedDomain(valueSeries.map(point => point.value)), [valueSeries]);

  // Table columns. `symbol` is captured for the amount suffix; USD formatter
  // changes each render, so it's a dep (cheap to rebuild).
  const symbol = data?.symbol ?? '';
  // The asset's current unit price (USD) — powers the per-buy profit column.
  const currentPriceUsd = data?.price ?? null;

  const walletColumns = useMemo<ColumnDef<CgAssetDetail['wallets'][number], any>[]>(
    () => [
      {
        accessorKey: 'name',
        header: translate('cryptoGest.transactions.wallet'),
        cell: info => info.getValue(),
        meta: { className: 'whitespace-nowrap font-medium text-foreground' }
      },
      {
        id: 'amount',
        accessorFn: row => parseFloat(row.amount),
        header: translate('cryptoGest.labels.amount'),
        cell: ({ row }) => `${amount(row.original.amount)} ${symbol}`,
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums' }
      },
      {
        accessorKey: 'avgCost',
        header: translate('cryptoGest.labels.avgCost'),
        cell: ({ getValue }) => usd(getValue() as number | null),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums text-muted-foreground' }
      },
      {
        accessorKey: 'price',
        header: translate('cryptoGest.labels.price'),
        cell: ({ getValue }) => usd(getValue() as number),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums text-muted-foreground' }
      },
      {
        accessorKey: 'valueUsd',
        header: translate('cryptoGest.labels.value'),
        cell: ({ getValue }) => usd(getValue() as number),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums font-medium' }
      },
      {
        accessorKey: 'pnlUsd',
        header: translate('cryptoGest.transactions.profitValue'),
        cell: ({ getValue }) => (
          <span className={signClass(getValue() as number | null)}>{usd(getValue() as number | null)}</span>
        ),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums font-medium' }
      }
    ],
    [translate, usd, symbol]
  );

  if (isLoading || !data) {
    return (
      <div className={'grid place-items-center py-20'}>
        <Spinner />
      </div>
    );
  }

  // Type filter options — only the types actually present, in canonical order.
  const typeOptions = ENTRY_TYPE_ORDER.filter(type => data.txTypes.includes(type)).map(type => ({
    value: type,
    label: translate(`cryptoGest.entryType.${type}`)
  }));
  const allTypeValues = typeOptions.map(option => option.value);
  // Absent param = all; "-" = none; otherwise the CSV (kept to present types).
  const typeFilter =
    typesParam === ''
      ? null
      : typesParam === '-'
      ? []
      : typesParam.split(',').filter(value => allTypeValues.includes(value));
  const selectedTypes = typeFilter ?? allTypeValues;

  // Persist the selection in the URL: all → clean (no param), none → "-".
  const onTypesChange = (next: string[]) => {
    if (next.length === 0) {
      setTypesParam('-');
    } else if (allTypeValues.every(value => next.includes(value))) {
      setTypesParam('');
    } else {
      setTypesParam(next.join(','));
    }
  };

  // Per-wallet tabs (rows fetched paginated + filtered on demand by the table).
  const txWallets = data.txWallets;
  const pageSize = 25;

  // Pre-select the wallet tab when arriving from a wallet (?wallet=<id>).
  const walletParam = searchParams.get('wallet');
  const defaultWalletTab =
    walletParam && txWallets.some(wallet => wallet.walletId === walletParam) ? walletParam : txWallets[0]?.walletId;

  return (
    <div className={'w-full space-y-6'}>
      <div>
        <div className={'flex items-center gap-2.5'}>
          <AssetIcon className={'h-8 w-8'} logo={data.logo} symbol={data.symbol} />

          <Title className={'pb-0'}>{data.symbol}</Title>
        </div>

        {data.name && <p className={'mt-1 text-sm text-muted-foreground'}>{data.name}</p>}
      </div>

      {/* Stats */}
      <div className={'grid grid-cols-2 gap-4 lg:grid-cols-4'}>
        <StatCard label={translate('cryptoGest.labels.value')} value={usd(data.valueUsd)} />

        <StatCard label={translate('cryptoGest.labels.amount')} value={`${amount(data.amount)} ${data.symbol}`} />

        <StatCard label={translate('cryptoGest.labels.avgCost')} value={usd(data.avgCost)} />

        <StatCard
          label={translate('cryptoGest.labels.profitRate')}
          sub={pct(data.profitRate)}
          subClass={signClass(data.unrealizedPnlUsd)}
          value={usd(data.unrealizedPnlUsd)}
        />
      </div>

      {/* Charts */}
      <div className={'grid grid-cols-1 gap-4 lg:grid-cols-2'}>
        <ChartCard
          action={
            <div className={'flex flex-wrap gap-1'}>
              {PRICE_STEPS.map(item => (
                <button
                  className={cn(
                    'rounded-md px-2 py-1 text-xs font-medium tabular-nums transition-colors',
                    step === item.key
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                  key={item.key}
                  onClick={() => selectStep(item.key)}
                  type={'button'}
                >
                  {item.label}
                </button>
              ))}
            </div>
          }
          title={translate('cryptoGest.labels.priceHistory')}
        >
          {priceSeries.length > 1 ? (
            <AreaTrendChart
              data={priceSeries}
              series={[{ key: 'price', label: data.symbol, color: chartColors.primary }]}
              valueFormatter={usd}
              xKey={'date'}
              yDomain={priceDomain}
            />
          ) : (
            <div className={'grid h-[300px] place-items-center text-center text-sm text-muted-foreground'}>
              {translate('cryptoGest.overview.noHistory')}
            </div>
          )}

          {/* Pan back/forward in time, one window at a time. */}
          <div className={'mt-2 flex items-center justify-center gap-3 text-xs'}>
            <button
              className={
                'rounded-md px-2 py-1 font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
              }
              onClick={() => setOffset(current => current + 1)}
              type={'button'}
            >
              {translate('cryptoGest.chart.older')}
            </button>

            <button
              className={cn(
                'rounded-md px-2 py-1 font-medium transition-colors',
                offset === 0
                  ? 'cursor-default text-muted-foreground/40'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
              disabled={offset === 0}
              onClick={() => setOffset(current => Math.max(0, current - 1))}
              type={'button'}
            >
              {translate('cryptoGest.chart.newer')}
            </button>
          </div>
        </ChartCard>

        <ChartCard title={translate('cryptoGest.labels.valueHistory')}>
          {valueSeries.length > 1 ? (
            <AreaTrendChart
              data={valueSeries}
              series={[{ key: 'value', label: translate('cryptoGest.labels.value'), color: chartColors.primary }]}
              valueFormatter={usd}
              xKey={'date'}
              yDomain={valueDomain}
            />
          ) : (
            <div className={'grid h-[300px] place-items-center text-center text-sm text-muted-foreground'}>
              {translate('cryptoGest.overview.noHistory')}
            </div>
          )}
        </ChartCard>
      </div>

      {/* Across wallets */}
      <div className={'overflow-hidden rounded-xl border border-border bg-card shadow-sm'}>
        <div className={'border-b border-border px-4 py-3 text-sm font-semibold text-foreground'}>
          {translate('cryptoGest.labels.acrossWallets')}
        </div>

        <DataTable columns={walletColumns} data={data.wallets} />
      </div>

      {/* Transactions per wallet (tabs) */}
      {txWallets.length > 0 && (
        <div className={'overflow-hidden rounded-xl border border-border bg-card shadow-sm'}>
          <div className={'flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3'}>
            <span className={'text-sm font-semibold text-foreground'}>
              {translate('cryptoGest.transactions.title')}
            </span>

            <div className={'flex flex-wrap items-center gap-4'}>
              <label className={'flex cursor-pointer items-center gap-2 text-sm text-muted-foreground'}>
                <Checkbox checked={showHidden} onCheckedChange={checked => setHiddenParam(checked ? '1' : '')} />

                {translate('cryptoGest.transactions.showHidden')}
              </label>

              <div className={'w-48'}>
                <MultiSelectFilter
                  allLabel={translate('cryptoGest.filter.all')}
                  clearLabel={translate('cryptoGest.filter.clear')}
                  label={translate('cryptoGest.filter.types')}
                  noneLabel={translate('cryptoGest.filter.none')}
                  onChange={onTypesChange}
                  options={typeOptions}
                  selectAllLabel={translate('cryptoGest.filter.selectAll')}
                  summary={(count, total) => translate('cryptoGest.filter.count', { count, total })}
                  value={selectedTypes}
                />
              </div>
            </div>
          </div>

          <div className={'p-4'}>
            <Tabs defaultValue={defaultWalletTab}>
              <TabsList>
                {txWallets.map(wallet => (
                  <TabsTrigger key={wallet.walletId} value={wallet.walletId}>
                    {wallet.name}
                  </TabsTrigger>
                ))}
              </TabsList>

              {txWallets.map(wallet => (
                <TabsContent key={wallet.walletId} value={wallet.walletId}>
                  <AssetWalletTransactions
                    assetId={data.assetId}
                    currentPriceUsd={currentPriceUsd}
                    includePool={showHidden}
                    pageSize={pageSize}
                    symbol={symbol}
                    types={typeFilter}
                    walletId={wallet.walletId}
                  />
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      )}
    </div>
  );
}
